import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  ButtonDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Label,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { editNumberFieldChange as editNumberFieldChangeAction } from '../../../actions/Numbers';
import { Api } from '../../../lib/api/api';
import remove from '../../../Res/Icon/remove.png';
import {
  PAGE_SIZE,
  boxSerialLabel,
  buildFormSync,
  getBoxToBoxHuntInvalidMessage,
  isHuntNumberType,
  mergeEligiblePages,
  resolveHuntPorts,
  resolveSelectedBoxSerials,
  restoreSelectedHuntBoxes,
  validateSelectedHuntBoxes,
} from './boxToBoxHuntHelpers';

const BoxToBoxHunt = () => {
  const dispatch = useDispatch();
  const api = useMemo(() => new Api(localStorage.getItem('jwtToken')), []);

  const numberByID = useSelector(state => state.AppState.numbers.numberByID);
  const type = useSelector(state => state.UIState.numbers.editNumber.type);
  const boxToBoxHuntInvalid = useSelector(state => !!state.UIState.numbers.editNumber.boxToBoxHuntInvalid);
  const huntPortsRaw = useSelector(state => state.UIState.numbers.editNumber.huntPorts);
  const selectedAta = useSelector(state => state.UIState.epikBoxDetails.selectedPort);
  const boxDetails = useSelector(state => state.AppState.boxDetails.box);

  const [eligible, setEligible] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const pageRef = useRef(1);
  const reqIdRef = useRef(0);
  const loadingRef = useRef(false);
  const searchTimerRef = useRef(null);

  const ringPorts = useMemo(
    () => resolveHuntPorts(huntPortsRaw, selectedAta?.boxPortNumber),
    [huntPortsRaw, selectedAta?.boxPortNumber],
  );

  const available = useMemo(
    () => eligible.filter(b => !selected.some(s => s.optionKey === b.optionKey)),
    [eligible, selected],
  );

  const setField = useCallback(
    (name, value) => dispatch(editNumberFieldChangeAction(name, value)),
    [dispatch],
  );

  const syncForm = useCallback(
    (nextSelected, nextEligible) => {
      const { huntBoxes, boxToBoxHuntSelections, boxToBoxHuntInvalid } = buildFormSync(nextSelected, nextEligible);
      setField('huntBoxes', huntBoxes);
      setField('boxToBoxHuntSelections', boxToBoxHuntSelections);
      setField('boxToBoxHuntInvalid', boxToBoxHuntInvalid);
    },
    [setField],
  );

  const loadPage = useCallback(
    async ({ page = 1, searchTerm = '', append = false } = {}) => {
      if (!isHuntNumberType(type) || !ringPorts.length || !boxDetails?._id) return;
      if (append && loadingRef.current) return;

      const reqId = ++reqIdRef.current;
      loadingRef.current = true;
      setLoading(true);
      if (!append) setEligible([]);
      try {
        const data = await api.boxes.getEligibleBoxToBoxHunt(boxDetails._id, {
          huntPorts: ringPorts,
          type,
          page,
          limit: PAGE_SIZE,
          search: searchTerm,
          numberId: numberByID?._id,
        });
        if (reqId !== reqIdRef.current) return;

        const docs = data?.docs || [];
        setEligible(prev => {
          const nextEligible = append ? mergeEligiblePages(prev, docs) : docs;
          setSelected(current => {
            const next = current.map(row => nextEligible.find(d => d.optionKey === row.optionKey) || row);
            syncForm(next, nextEligible);
            return next;
          });
          return nextEligible;
        });
        setHasMore(Boolean(data?.hasMore));
        setLoaded(true);
        pageRef.current = page;
      } catch (_) {
        if (reqId === reqIdRef.current && !append) {
          setEligible([]);
          setLoaded(false);
          setHasMore(false);
        }
      } finally {
        if (reqId === reqIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [api, boxDetails, numberByID, ringPorts, syncForm, type],
  );

  useEffect(() => {
    if (!isHuntNumberType(type)) {
      reqIdRef.current += 1;
      setField('boxToBoxHuntSelections', []);
      setField('boxToBoxHuntInvalid', false);
      setEligible([]);
      setSelected([]);
      setLoaded(false);
      setHasMore(false);
      setSearch('');
      pageRef.current = 1;
      return;
    }

    reqIdRef.current += 1;
    pageRef.current = 1;
    setLoaded(false);
    setEligible([]);
    setHasMore(false);
    setSearch('');

    const restored = restoreSelectedHuntBoxes(numberByID?.boxToBoxHuntSelections, ringPorts, numberByID);
    const reqId = reqIdRef.current;
    setSelected(restored);
    syncForm(restored, null);
    (async () => {
      const withSerials = await resolveSelectedBoxSerials(api, restored);
      if (reqId !== reqIdRef.current) return;
      setSelected(withSerials);

      if (!withSerials.length) {
        setField('boxToBoxHuntInvalid', false);
        return;
      }
      const invalid = await validateSelectedHuntBoxes({
        api,
        parentBoxId: boxDetails?._id,
        huntPorts: ringPorts,
        type,
        numberId: numberByID?._id,
        selected: withSerials,
      });
      if (reqId === reqIdRef.current) setField('boxToBoxHuntInvalid', invalid);
    })();
  }, [type, numberByID?._id, numberByID?.boxToBoxHuntSelections, ringPorts, api, boxDetails?._id, setField, syncForm]);

  useEffect(() => {
    if (!open || loading || !loaded || !hasMore || available.length) return;
    loadPage({ page: pageRef.current + 1, searchTerm: search, append: true });
  }, [open, loading, loaded, hasMore, available.length, loadPage, search]);

  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      if (next) {
        pageRef.current = 1;
        setSearch('');
        loadPage({ page: 1, searchTerm: '', append: false });
      }
      return next;
    });
  };

  const onSearch = e => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(searchTimerRef.current);
    reqIdRef.current += 1;
    loadingRef.current = false;
    setEligible([]);
    setLoaded(false);
    setLoading(true);
    searchTimerRef.current = setTimeout(() => {
      pageRef.current = 1;
      loadPage({ page: 1, searchTerm: value.trim(), append: false });
    }, 200);
  };

  const onScroll = e => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24 && hasMore && !loading) {
      loadPage({ page: pageRef.current + 1, searchTerm: search, append: true });
    }
  };

  const pick = key => {
    const entry = eligible.find(b => b.optionKey === key);
    if (!entry || selected.some(b => b.optionKey === entry.optionKey)) return;
    const next = [...selected, entry];
    setSelected(next);
    syncForm(next, eligible);
    setOpen(false);
    setSearch('');
  };

  const removeAt = index => {
    const next = selected.filter((_, i) => i !== index);
    setSelected(next);
    syncForm(next, loaded ? eligible : null);
    if (!next.length) {
      setField('boxToBoxHuntInvalid', false);
      return;
    }
    validateSelectedHuntBoxes({
      api,
      parentBoxId: boxDetails?._id,
      huntPorts: ringPorts,
      type,
      numberId: numberByID?._id,
      selected: next,
    }).then(invalid => setField('boxToBoxHuntInvalid', invalid));
  };

  if (!isHuntNumberType(type)) return null;

  return (
    <>
      <Label for="boxToBoxHunt" sm={12} className="adv-modem-config mt-3">
        Box-to-Box Hunt Configuration
      </Label>
      <ButtonDropdown isOpen={open} toggle={toggle} style={{ width: '100%' }}>
        <DropdownToggle caret style={{ width: '100%', textAlign: 'left', textOverflow: 'ellipsis', overflow: 'hidden' }}>
          Select
        </DropdownToggle>
        <DropdownMenu style={{ width: '100%', padding: 0 }}>
          <div className="px-2 py-2" onClick={e => e.stopPropagation()}>
            <Input type="search" placeholder="Search by serial number" value={search} onChange={onSearch} />
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }} onScroll={onScroll}>
            {loading && !available.length ? (
              <DropdownItem disabled>{search.trim() ? 'Searching…' : 'Loading boxes…'}</DropdownItem>
            ) : !loaded && !loading ? (
              <DropdownItem disabled>Open to load matching boxes</DropdownItem>
            ) : !available.length ? (
              <DropdownItem disabled>
                {search.trim() ? `No box found for "${search.trim()}"` : hasMore ? 'Loading more boxes…' : 'No more boxes to add'}
              </DropdownItem>
            ) : (
              available.map(box => (
                <DropdownItem key={box.optionKey} onClick={() => pick(box.optionKey)}>
                  {boxSerialLabel(box) || box.serialNumber}
                </DropdownItem>
              ))
            )}
            {loading && available.length > 0 && <DropdownItem disabled>Loading more…</DropdownItem>}
          </div>
        </DropdownMenu>
      </ButtonDropdown>
      {boxToBoxHuntInvalid && (
        <Alert color="warning" className="mt-2 mb-0">
          {getBoxToBoxHuntInvalidMessage(type)}
        </Alert>
      )}
      {selected.length > 0 && (
        <div className="mt-2">
          <Label sm={12}>Selected boxes</Label>
          <div className="email-list">
            {selected.map((box, idx) => (
              <div className="multi-input-item" key={box.optionKey || idx}>
                <span className="item">{boxSerialLabel(box) || 'Loading…'}</span>
                <img className="remove-icon" src={remove} onClick={() => removeAt(idx)} alt="remove" />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default BoxToBoxHunt;
